/* cspell:disable */
import { Button, Check, DatePicker, Input, mergeClassName as cn, Pencil, Trash, X } from '@chain-zen/ui'
import { format, parseISO } from 'date-fns'
import { useEffect, useState } from 'react'

import type { IDateTime } from '../../../types/post'

interface EditableDateTimeFieldProps {
  dateTime: IDateTime | null
  disabled?: boolean
  onUpdate: (dateTime: IDateTime | null) => void
  className?: string
  placeholder?: string
  showEditIcon?: boolean
}

// Convert 12-hour format to 24-hour format for input
const convertTo24Hour = (time12: string): string => {
  if (!time12) return ''

  // If it's already in 24-hour format, return as is
  if (!time12.includes('AM') && !time12.includes('PM')) {
    return time12
  }

  const [time, ampm] = time12.split(' ')
  const [hours, minutes] = time.split(':')
  let hour24 = parseInt(hours, 10)

  if (ampm === 'AM' && hour24 === 12) {
    hour24 = 0
  } else if (ampm === 'PM' && hour24 !== 12) {
    hour24 += 12
  }

  return `${hour24.toString().padStart(2, '0')}:${minutes}`
}

// Convert input time (24-hour) to 12-hour format for saving
const convertTo12Hour = (time24: string): string => {
  if (!time24) return ''

  const [hours, minutes] = time24.split(':')
  const hour24 = parseInt(hours, 10)
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
  const ampm = hour24 < 12 ? 'AM' : 'PM'
  return `${hour12}:${minutes} ${ampm}`
}

export function EditableDateTimeField({
  dateTime,
  onUpdate,
  disabled,
  className,
  placeholder = 'No date/time set',
  showEditIcon = true
}: EditableDateTimeFieldProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [tempDate, setTempDate] = useState<Date | undefined>(dateTime?.date ? parseISO(dateTime.date) : undefined)
  const [tempTime, setTempTime] = useState(dateTime?.time ? convertTo24Hour(dateTime.time) : '')

  // Reset temp values when dateTime prop changes (external update)
  useEffect(() => {
    if (!isEditing) {
      setTempDate(dateTime?.date ? parseISO(dateTime.date) : undefined)
      // Convert 12-hour format to 24-hour for the input
      setTempTime(dateTime?.time ? convertTo24Hour(dateTime.time) : '')
    }
  }, [dateTime, isEditing])

  const handleSave = () => {
    if (tempDate || tempTime) {
      const dateToUse = tempDate ? format(tempDate, 'yyyy-MM-dd') : dateTime?.date || format(new Date(), 'yyyy-MM-dd')
      // Convert 24-hour input time to 12-hour format for saving
      const timeToSave = tempTime ? convertTo12Hour(tempTime) : undefined

      onUpdate({
        date: dateToUse,
        time: timeToSave
      })
    } else {
      onUpdate(null)
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    // Reset to original values
    setTempDate(dateTime?.date ? parseISO(dateTime.date) : undefined)
    setTempTime(dateTime?.time ? convertTo24Hour(dateTime.time) : '')
    setIsEditing(false)
  }

  const handleClearAll = () => {
    onUpdate(null)
    setIsEditing(false)
  }

  const handleClearTime = () => {
    setTempTime('')
  }

  const handleStartEdit = () => {
    if (!disabled) {
      setIsEditing(true)
    }
  }

  const handleDateChange = (newDate: Date | undefined) => {
    setTempDate(newDate)
  }

  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Store the 24-hour format value from the input
    setTempTime(e.target.value)
  }

  const handleTimeInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  // Format display value
  const getDisplayValue = () => {
    if (!dateTime?.date && !dateTime?.time) {
      return placeholder
    }

    const displayDate = dateTime?.date ? format(parseISO(dateTime.date), 'MMM dd, yyyy') : ''
    const displayTime = dateTime?.time ? formatTimeDisplay(dateTime.time) : ''

    if (displayDate && displayTime) {
      return `${displayDate} at ${displayTime}`
    }
    return displayDate || displayTime
  }

  // Convert 12-hour format to display format
  const formatTimeDisplay = (time: string) => {
    if (!time) return ''

    // If it's already in 12-hour format, return as is
    if (time.includes('AM') || time.includes('PM')) {
      return time
    }

    // Convert 24-hour format to 12-hour for display
    const [hours, minutes] = time.split(':')
    const hour24 = parseInt(hours, 10)
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    const ampm = hour24 < 12 ? 'AM' : 'PM'
    return `${hour12}:${minutes} ${ampm}`
  }

  if (isEditing) {
    return (
      <div className={cn('flex flex-col sm:flex-row sm:items-center gap-2', className)}>
        <DatePicker
          date={tempDate}
          onSelect={handleDateChange}
          disabled={disabled}
          className="h-8 text-xs w-full sm:w-auto sm:flex-1"
          placeholder="Select date"
        />

        <div className="flex gap-2 sm:flex-none">
          <div className="relative flex-1 sm:w-[140px]">
            <Input
              type="time"
              value={tempTime}
              onChange={handleTimeInputChange}
              onKeyDown={handleTimeInputKeyDown}
              disabled={disabled}
              className="h-8 text-xs w-full pr-14"
              placeholder="Enter time"
            />
            {!!tempTime && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearTime}
                className="absolute right-6 top-0 h-8 w-6 p-0 hover:bg-transparent"
                title="Clear time"
              >
                <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
              </Button>
            )}
          </div>

          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" onClick={handleSave} className="h-8 w-8 p-0" title="Save changes">
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleClearAll} className="h-8 w-8 p-0" title="Clear date/time">
              <Trash className="h-4 w-4 text-orange-600" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel} className="h-8 w-8 p-0" title="Cancel changes">
              <X className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between group cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded transition-colors',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      onClick={handleStartEdit}
    >
      <span className={cn('text-sm', !dateTime?.date && !dateTime?.time && 'text-gray-500 dark:text-gray-400 italic')}>
        {getDisplayValue()}
      </span>

      {!!showEditIcon && !disabled && (
        <Pencil className="h-3 w-3 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  )
}
